// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

/*
 * Human-facing naming for cockpit surfaces.
 *
 * The rule this module exists to enforce: a reviewer never reads an internal
 * identifier when a name, title, intent, or repository is available, and never
 * reads a placeholder standing in for one that is not. When there is no human
 * reference, these helpers return null so the caller omits the element rather
 * than printing filler such as "Code review" beside a revision hash.
 */

export type StateTone = "active" | "attention" | "critical" | "neutral";

const STATE_LABELS: Readonly<Record<string, string>> = {
  "accepted-risk": "Risk accepted",
  cancelled: "Cancelled",
  complete: "Complete",
  completed: "Complete",
  conflicted: "Needs resolution",
  current: "Up to date",
  "current-code-ready": "Ready",
  degraded: "Limited",
  failed: "Failed",
  "in-progress": "In progress",
  limited: "Limited",
  "not-built": "Not available",
  "not-started": "Not started",
  partial: "Partial",
  paused: "Paused",
  pending: "Syncing",
  ready: "Ready",
  rejected: "Rejected",
  resumed: "In progress",
  running: "In progress",
};

const REVIEW_RUN_LABELS: Readonly<Record<string, string>> = {
  cancelled: "Analysis cancelled",
  cancelling: "Cancelling analysis",
  completed: "Analysis ready",
  failed: "Analysis failed",
  investigating: "Analyzing",
  "not-started": "Not analyzed",
  partial: "Analysis incomplete",
  planned: "Analysis planned",
  registered: "Ready to analyze",
  resuming: "Resuming analysis",
  superseded: "Superseded",
  verifying: "Verifying evidence",
};

/*
 * Tone is a second channel behind an icon and a word, never the only signal.
 * "Fine" is deliberately absent: healthy states render neutral so a tinted
 * row always means the reviewer should look.
 */
const CRITICAL_STATES = new Set([
  "cancelled",
  "conflicted",
  "failed",
  "rejected",
]);

const ATTENTION_STATES = new Set([
  "degraded",
  "limited",
  "not-built",
  "not-started",
  "partial",
  "paused",
  "pending",
  "superseded",
]);

const ACTIVE_STATES = new Set([
  "cancelling",
  "in-progress",
  "investigating",
  "resumed",
  "resuming",
  "running",
  "verifying",
]);

export function displayState(state: string): string {
  return (
    STATE_LABELS[state] ??
    state
      .split("-")
      .filter(Boolean)
      .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
      .join(" ")
  );
}

export function displayReviewRunState(state: string): string {
  return REVIEW_RUN_LABELS[state] ?? displayState(state);
}

export function stateTone(state: string): StateTone {
  if (CRITICAL_STATES.has(state)) return "critical";
  if (ATTENTION_STATES.has(state)) return "attention";
  if (ACTIVE_STATES.has(state)) return "active";
  return "neutral";
}

/*
 * The declared intent's first line is the change's title, exactly as its
 * author wrote it. Everything else in the queue is secondary to this string.
 */
export function reviewTitle(summary: string): string {
  return (
    summary
      .split(/\r?\n/u)
      .map((line) => line.trim())
      .find(Boolean) ?? "Change without a declared title"
  );
}

export function reviewDescription(summary: string): string | null {
  const lines = summary
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.length > 1 ? lines.slice(1).join(" ") : null;
}

/*
 * A provider reference a human would recognize, such as "#412". Returns null
 * when the series carries no provider identity, so callers omit the element
 * instead of printing a generic label next to an opaque identifier.
 */
export function reviewReference(reviewSeriesId: string): string | null {
  const pullRequest = /^(?:github|gitlab)-(?:pr|mr):[^:]+:(\d+)$/u.exec(
    reviewSeriesId,
  );
  return pullRequest?.[1] === undefined ? null : `#${pullRequest[1]}`;
}

/*
 * The repository a change belongs to, as "owner/name". Falls back to the
 * connection's own display name, which onboarding requires a human to set.
 */
export function repositoryName(
  repositoryUrl: string,
  displayName: string,
): string {
  const trimmed = repositoryUrl
    .trim()
    .replace(/\.git$/u, "")
    .replace(/\/$/u, "");
  const segments = trimmed.split("/").filter(Boolean);
  const name = segments.at(-1);
  const owner = segments.at(-2);
  if (name === undefined || owner === undefined || owner.includes(":")) {
    return displayName;
  }
  return `${owner}/${name}`;
}

export function shortIdentifier(value: string, length = 8): string {
  return value.length <= length ? value : value.slice(0, length);
}

/* Counts a reviewer reads aloud. "1 finding", never "1 findings". */
export function pluralize(count: number, singular: string, plural?: string) {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}

export function countLabel(
  count: number,
  singular: string,
  plural?: string,
): string {
  return `${String(count)} ${pluralize(count, singular, plural)}`;
}

/* Compact size of a change, for scanning a queue. */
export function changeScale(additions: number, deletions: number): string {
  const total = additions + deletions;
  if (total === 0) return "no line changes";
  if (total < 30) return "small";
  if (total < 250) return "medium";
  return "large";
}

export function relativeTime(value: string, now = Date.now()): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return "Unknown time";
  }
  const seconds = Math.round((timestamp - now) / 1000);
  const ranges: readonly [number, Intl.RelativeTimeFormatUnit][] = [
    [60, "second"],
    [60, "minute"],
    [24, "hour"],
    [7, "day"],
    [4.345, "week"],
    [12, "month"],
    [Number.POSITIVE_INFINITY, "year"],
  ];
  let amount = seconds;
  for (const [limit, unit] of ranges) {
    if (Math.abs(amount) < limit) {
      return new Intl.RelativeTimeFormat(undefined, {
        numeric: "auto",
      }).format(Math.round(amount), unit);
    }
    amount /= limit;
  }
  return new Date(timestamp).toLocaleDateString();
}
